import { useState } from "react";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { LEGAL_DOCS } from "@/data/legal";

const PERFIS = ["Passageiro", "Motorista", "Empresário"];
const TIPOS = LEGAL_DOCS["excluir-conta"].sections[0].list.map((s) => s.replace(/;$/, ""));

export default function ExcluirConta() {
  const [form, setForm] = useState({
    nome: "",
    email: "",
    perfil: "",
    tipo_solicitacao: "",
    telefone: "",
    observacoes: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [protocol, setProtocol] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 3) e.nome = "Informe seu nome completo.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Informe o e-mail vinculado à conta.";
    if (!form.perfil) e.perfil = "Selecione o perfil.";
    if (!form.tipo_solicitacao) e.tipo_solicitacao = "Selecione o tipo de solicitação.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/excluir-conta", form);
      setProtocol(data.protocolo);
    } catch (err) {
      setServerError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Privacidade e dados"
        title="Exclusão de Conta e Dados — OFF360 / 360Taxi"
        lead="Você pode solicitar a exclusão da sua conta e dos dados pessoais associados aos serviços do OFF360, respeitadas as hipóteses de conservação legalmente permitidas. Para iniciar sua solicitação, preencha o formulário abaixo."
      />

      <section className="mx-auto max-w-8xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_0.85fr]">
          <Reveal>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10" data-testid="excluir-conta-form-card">
              {protocol ? (
                <div className="py-8 text-center" data-testid="deletion-protocol-result">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-brand-green" />
                  <h3 className="mt-4 font-display text-2xl font-bold text-slate-900">Solicitação recebida!</h3>
                  <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                    Seu pedido foi registrado. Guarde o número de protocolo apresentado para acompanhar o
                    atendimento.
                  </p>
                  <p className="mx-auto mt-6 w-fit rounded-full bg-brand-obsidian px-8 py-3 font-mono text-lg font-bold text-brand-amber">
                    {protocol}
                  </p>
                  <p className="mx-auto mt-6 max-w-md text-xs leading-relaxed text-slate-500">
                    A titularidade será verificada de forma segura antes de qualquer exclusão sensível. A equipe
                    responsável poderá solicitar informações adicionais.
                  </p>
                  <button
                    type="button"
                    data-testid="excluir-conta-nova-solicitacao"
                    onClick={() => {
                      setProtocol("");
                      setForm({ nome: "", email: "", perfil: "", tipo_solicitacao: "", telefone: "", observacoes: "" });
                    }}
                    className="mt-8 rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-slate-700 transition-colors hover:border-brand-orange hover:text-brand-orange"
                  >
                    Enviar outra solicitação
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="excluir-conta-form">
                  <h2 className="font-display text-2xl font-bold text-slate-900">Formulário de solicitação</h2>
                  <p className="mt-2 text-sm text-slate-500">
                    O formulário funciona publicamente, sem exigir login. A titularidade será verificada antes de
                    qualquer exclusão sensível.
                  </p>

                  <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Nome completo *" error={errors.nome}>
                      <input data-testid="excluir-conta-nome-input" type="text" value={form.nome} onChange={set("nome")} className="input-off" placeholder="Seu nome completo" />
                    </Field>
                    <Field label="E-mail vinculado à conta *" error={errors.email}>
                      <input data-testid="excluir-conta-email-input" type="email" value={form.email} onChange={set("email")} className="input-off" placeholder="voce@email.com" />
                    </Field>
                    <Field label="Perfil *" error={errors.perfil}>
                      <select data-testid="excluir-conta-perfil-select" value={form.perfil} onChange={set("perfil")} className="input-off">
                        <option value="">Selecione</option>
                        {PERFIS.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Tipo de solicitação *" error={errors.tipo_solicitacao}>
                      <select data-testid="excluir-conta-tipo-select" value={form.tipo_solicitacao} onChange={set("tipo_solicitacao")} className="input-off">
                        <option value="">Selecione</option>
                        {TIPOS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Telefone cadastrado (opcional)">
                        <input data-testid="excluir-conta-telefone-input" type="tel" value={form.telefone} onChange={set("telefone")} className="input-off" placeholder="(00) 00000-0000" />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Observações (opcional)">
                        <textarea data-testid="excluir-conta-observacoes-input" rows={4} value={form.observacoes} onChange={set("observacoes")} className="input-off resize-y" placeholder="Informações adicionais, se necessário." />
                      </Field>
                    </div>
                  </div>

                  {serverError && (
                    <p className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="excluir-conta-server-error">
                      {serverError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="deletion-form-submit"
                    className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-4 text-base font-bold text-white shadow-lg shadow-brand-orange/25 transition-all hover:bg-brand-orangedark disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
                    Enviar solicitação
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-7">
                <h3 className="font-display text-lg font-bold text-slate-900">Como sua solicitação é tratada</h3>
                <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-slate-600">
                  {LEGAL_DOCS["excluir-conta"].sections[1].list.map((li) => (
                    <li key={li} className="flex items-start gap-2.5">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                      {li.replace(/;$/, "")}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-amber-300 bg-amber-50 p-6">
                <ShieldAlert className="h-6 w-6 shrink-0 text-amber-600" />
                <p className="text-sm font-semibold leading-relaxed text-amber-900">
                  Nenhuma exclusão irreversível é executada apenas com base em um e-mail informado por terceiro,
                  sem verificação adequada de identidade.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-7">
                <h3 className="font-display text-lg font-bold text-slate-900">Retenção legal</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {LEGAL_DOCS["excluir-conta"].sections[4].paras[0]}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-xs font-semibold text-red-600">{error}</span>}
    </label>
  );
}
